import { Incentive } from './plan';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { useTranslation } from 'react-i18next';
import { toCurrency } from '@app/i18n';

import { Session } from './session';
import { queryGet, queryReplace } from './fetch';
import { Participation, useSessionLink } from '.';
import { TFunction } from 'i18next';

export const useIncentiveForSession = (
  id?: string,
  country?: string,
  options: UseQueryOptions<Incentive> = {},
) => {
  const enabled =
    options.enabled === undefined ? !!id : options.enabled && !!id;

  return useQuery<Incentive>(
    ['sessions', id, 'incentive', country],
    queryGet(`/v2/sessions/${id}/incentive`, { country }),
    {
      retry: false,
      ...options,
      enabled,
    },
  );
};

export const useUpdateParticipationIncentive = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Incentive, any, Incentive>(
    queryReplace(`/v2/participations/${id}/incentive`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['participations', id]);
      },
    },
  );
};

export const useParticipationIncentiveText = (
  participation?: Participation,
  session?: Session,
  locale?: string,
  short?: boolean,
) => {
  const { data: incentive } = useIncentiveForSession(
    participation?.sessionLinkId || session?.id,
    undefined,
    {
      enabled: !participation?.incentive,
    },
  );
  const { data: sessionLink } = useSessionLink(participation?.sessionLinkId);

  return useTranslateIncentiveText(
    participation?.incentive || incentive,
    locale,
    session,
    short,
    participation?.incentiveText || sessionLink?.incentiveText,
  );
};

export const useIncentiveText = (
  sessionLinkId?: string,
  session?: Session,
  locale?: string,
  short?: boolean,
  countryKey?: string,
) => {
  const { data: incentive } = useIncentiveForSession(
    sessionLinkId || session?.id,
    countryKey,
  );
  const { data: sessionLink } = useSessionLink(sessionLinkId);
  return useTranslateIncentiveText(
    incentive,
    locale,
    session,
    short,
    sessionLink?.incentiveText,
  );
};

export const useTranslateIncentiveText = (
  incentive?: Incentive,
  locale?: string,
  session?: Session,
  short?: boolean,
  text?: string,
) => {
  const { t } = useTranslation();
  if (text) {
    return text;
  }
  return translateIncentiveText(t, incentive, locale, session, short);
};

export const translateIncentiveText = (
  t: TFunction,
  incentive?: Incentive,
  locale?: string,
  session?: Session,
  short?: boolean,
) => {
  if (!incentive) {
    return session ? session.incentiveText : '';
  }

  if (short) {
    return toCurrency(incentive.amount, true, incentive.currencyCode, locale);
  }

  return t(`incentives.GIFT_CARD`, {
    amount: incentive.amount,
    lng: locale,
    currency: incentive.currencyCode,
  });
};
